import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import {
    type CollectionChainType,
    type CollectionSalesVolume,
    type DaasMarketplaceCollection,
    type FloorpriceHistoryItem,
} from '@app/client/common/structs';

export interface FreshCollection extends DaasMarketplaceCollection {
    salesVolume?: CollectionSalesVolume;
    floorPriceChange?: FloorpriceHistoryItem;
    chain?: CollectionChainType;
    valid?: boolean;
}

export type FreshDataBoardData = {
    collections: FreshCollection[];
    queuedCollections: FreshCollection[];
    newCollections: FreshCollection[];
    canShowNewBatch?: boolean;
    isInitialBatch?: boolean;
};

interface FreshDataBoardState extends FreshDataBoardData {
    addCollectionToBatch: (collection: FreshCollection) => void;
    addCollectionsToBatch: (collection: FreshCollection[]) => void;
    addCollections: (collections: FreshCollection[]) => void;
    setShowNewBatch: () => void;
    clearData: () => void;
}

const initialState: FreshDataBoardData = {
    collections: [],
    queuedCollections: [],
    newCollections: [],
    canShowNewBatch: false,
    isInitialBatch: true,
};

export const useFantasyFreshDataBoard = create<FreshDataBoardState>()(
    devtools(
        (set, get) => ({
            ...initialState,
            addCollectionToBatch: (collection) =>
                set((state) => {
                    const existsOnCollections = state.collections?.find((col) => col.slug === collection.slug);
                    const existsInQueued = state.queuedCollections?.find((col) => col.slug === collection.slug);
                    const exists = existsOnCollections || existsInQueued;
                    if (!exists) {
                        const newQueued = [collection, ...state.queuedCollections].slice(0, 100);
                        if (newQueued?.length >= 3) {
                            // if there are 3 or more, add to new batch
                            return {
                                newCollections: [...newQueued, ...state.newCollections],
                                queuedCollections: [],
                                canShowNewBatch: true,
                            };
                        }
                        //otherwise store in queued
                        return { queuedCollections: newQueued };
                    } else {
                        return { queuedCollections: state.queuedCollections };
                    }
                }),
            addCollectionsToBatch: (collections) => {
                for (const collection of collections) {
                    get().addCollectionToBatch(collection);
                }
            },
            addCollections: (newCollections) =>
                set((state) => {
                    let updatedCollections = state.collections;
                    newCollections.forEach((collection) => {
                        const exists = !!state.collections?.find((col) => collection.slug === col.slug);
                        if (!exists) {
                            updatedCollections = [collection, ...updatedCollections].slice(0, 100);
                        }
                    });

                    return { collections: updatedCollections, newCollections: [] };
                }),
            // if using showNext batch add the new items to entire collections store
            setShowNewBatch: () =>
                set((state) => {
                    state.addCollections(state.newCollections);
                    return { canShowNewBatch: false, newCollections: [], isInitialBatch: false };
                }),
            clearData: () => set(initialState),
        }),
        { name: 'fantasy-fresh-data-storage' },
    ),
);
